import { useEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, Surface, Text, useTheme } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { createCheckoutSession, createSelfPayBillingPortalSession, createSubscription } from '@/src/api/apiService';
import { API_BASE, APP_ORIGIN } from '@/src/api/config';
import { resolveEnvironment, track } from '@/src/lib/analytics';
import { billingCycleForPlan, featureForPlan } from '@/src/features/onboarding/ownerPlanSelection';
import { GpsContractDeclinedNotice } from '@/src/features/onboarding/gpsDecline/GpsContractDeclinedNotice';
import { usePaywallStatus } from '@/src/hooks/usePaywallStatus';
import { useGpsContractGate } from '@/src/hooks/useGpsContractGate';
import { PlanSelectionModal } from '@/src/components/PlanSelectionModal';
import { HandlePayment } from '@/src/components/HandlePayment';

interface Plan {
  id: string;
  price_id: string;
  unit_amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  product: { product_id: string; name: string; description: string; metadata: any };
  metadata: any;
}

/**
 * D-0 hard block (#209, Q2 d0_behavior="block"): the trial ended without a
 * payment method. Owners land here (dashboard/_layout gate) and can only
 * leave by paying — PlanSelectionModal with isTrial={false} so the flow goes
 * straight to a paid subscription (no trial farming), card collected in-app
 * via HandlePayment on web (setup-intent decision, Ronald 2026-07-06) and
 * hosted Stripe Checkout on mobile.
 *
 * Drivers past the company grace (Q6 "blocked") see the explanation; their
 * self-pay CTA arrives with #207.
 */
export default function PaywallExpiredScreen() {
  const { t } = useTranslation('paywall');
  const theme = useTheme();
  const router = useRouter();
  const { status, refetch } = usePaywallStatus();

  const [plansVisible, setPlansVisible] = useState(false);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ plan: Plan; quantity: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [declinedFrom, setDeclinedFrom] = useState<{
    feature: string;
    billing: string;
    licenses: number;
  } | null>(null);
  const gpsContractGate = useGpsContractGate();

  const isOwner = status?.is_owner ?? true;
  const paymentIssue = status?.payment_action_required === true && status.is_self_pay === true;

  const fixSelfPayPayment = async () => {
    setError(null);
    try {
      const frontendOrigin = Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : APP_ORIGIN;
      const response = await createSelfPayBillingPortalSession(
        `${frontendOrigin}/dashboard/home`,
        `${API_BASE}/api/subscriptions/billing_portal/`,
        Platform.OS !== 'web',
      );
      if (!response.ok || !response.data?.url) {
        throw new Error(response.errorMessage || 'Unable to open billing portal');
      }
      await Linking.openURL(response.data.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('expired.paymentError', 'Não foi possível abrir a gestão de pagamento.'));
    }
  };

  useEffect(() => {
    track('paywall_expired_shown', {
      user_role: isOwner ? 'owner' : 'driver',
      state: status?.state,
    });
    // Mount-only: the impression event must not re-fire on status refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If payment landed (or state changed for any reason), let the user back in.
  useEffect(() => {
    if (status && ['active', 'trialing'].includes(status.state)) {
      router.replace('/dashboard/home');
    }
  }, [status, router]);

  const handlePlanSelected = async (plan: Plan, quantity: number) => {
    setError(null);
    const planFeature = featureForPlan(plan);
    const planBilling = billingCycleForPlan(plan);
    if (declinedFrom && planFeature === 'hours') {
      track('gps_contract_declined_to_hours', {
        screen: 'paywall_expired',
        feature: declinedFrom.feature,
        billing: declinedFrom.billing,
        licenses: declinedFrom.licenses,
        user_role: 'owner',
        app_variant: resolveEnvironment(),
      });
      setDeclinedFrom(null);
    }
    // D-059 / ALL-125: GPS/Combo needs the contract accepted for this purchase before paying.
    const outcome = await gpsContractGate.requestContract(plan, quantity, {
      onAccepted: () => {
        setDeclinedFrom(null);
        return startPaidCheckout(plan, quantity);
      },
      onCancelled: reason => {
        if (reason !== 'rejected') return;
        // ALL-134: nothing was charged; the plan list comes back with a neutral notice.
        const declined = {
          feature: planFeature ?? 'gps',
          billing: planBilling ?? 'monthly',
          licenses: quantity,
        };
        setDeclinedFrom(declined);
        track('gps_contract_declined', {
          screen: 'paywall_expired',
          ...declined,
          user_role: 'owner',
          app_variant: resolveEnvironment(),
        });
      },
    });
    if (outcome === 'load_error') {
      setError(gpsContractGate.loadErrorMessage);
      return;
    }
    if (outcome === 'shown') return;
    await startPaidCheckout(plan, quantity);
  };

  const startPaidCheckout = async (plan: Plan, quantity: number) => {
    try {
      if (Platform.OS === 'web') {
        // Web: subscription + in-app card entry (setup-intent path).
        const response = await createSubscription({
          price_id: plan.price_id,
          quantity,
        });
        if (!response.ok || !response.data.client_secret) {
          throw new Error(t('expired.subscriptionError', 'Não foi possível criar a assinatura.'));
        }
        setSelected({ plan, quantity });
        setClientSecret(response.data.client_secret);
        setSubscriptionId(response.data.subscription_id);
        setPlansVisible(false);
        setPaymentVisible(true);
        return;
      }

      // Mobile: hosted Stripe Checkout via auth session.
      const frontendReturnUrl = Linking.createURL('/dashboard/home');
      const response = await createCheckoutSession({
        price_id: plan.price_id,
        quantity,
        return_url: `${API_BASE}/api/subscriptions/stripe-redirect/?mobile=true`,
        frontend_return_url: frontendReturnUrl,
        is_mobile: true,
      });
      if (!response.ok || !response.data?.checkout_url) {
        throw new Error(t('expired.subscriptionError', 'Não foi possível criar a assinatura.'));
      }
      setPlansVisible(false);
      const result = await WebBrowser.openAuthSessionAsync(response.data.checkout_url, frontendReturnUrl);
      if (result.type === 'success' || result.type === 'dismiss') {
        await refetch();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('expired.subscriptionError', 'Não foi possível criar a assinatura.'));
    }
  };

  const handlePaymentSuccess = async () => {
    track('card_added', { user_role: 'owner', source: 'paywall_expired' });
    await AsyncStorage.setItem('ownerHasLicenses', 'true');
    setPaymentVisible(false);
    await refetch();
    router.replace('/dashboard/home');
  };

  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.content}>
        <Text
          variant="headlineSmall"
          style={[styles.title, { color: theme.colors.error }]}
          accessibilityRole="header"
          testID="paywall-expired-title"
        >
          {isOwner
            ? t('expired.titleOwner', 'O período de teste terminou')
            : paymentIssue
              ? t('expired.titlePaymentIssue', 'A sua licença precisa de atenção')
            : t('expired.titleDriver', 'O acesso da sua empresa terminou')}
        </Text>
        <Text variant="bodyMedium" style={[styles.body, { color: theme.colors.onSurfaceVariant }]}>
          {isOwner
            ? t(
                'expired.bodyOwner',
                'Tal como avisámos na timeline do teste, para continuar a usar o AllDrivers é preciso ativar a subscrição. Os seus dados estão guardados.'
              )
            : paymentIssue
              ? t(
                  'expired.bodyPaymentIssue',
                  'A sua licença não está ativa porque o pagamento precisa de atenção. Atualize o método de pagamento para recuperar o acesso.'
                )
            : t(
                'expired.bodyDriver',
                'O período de tolerância da sua empresa terminou. Pague a sua própria licença para continuar a conduzir — ou fale com o seu gestor.'
              )}
        </Text>

        {isOwner ? (
          <Button
            mode="contained"
            onPress={() => setPlansVisible(true)}
            style={styles.cta}
            contentStyle={styles.ctaContent}
            testID="paywall-expired-cta"
          >
            {t('expired.ctaOwner', 'Ativar subscrição')}
          </Button>
        ) : paymentIssue ? (
          <Button
            mode="contained"
            onPress={() => { void fixSelfPayPayment(); }}
            style={styles.cta}
            contentStyle={styles.ctaContent}
            testID="paywall-payment-issue-cta"
          >
            {t('expired.fixPaymentCta', 'Corrigir pagamento')}
          </Button>
        ) : (
          // Q6 takeover (#207): the blocked driver's way out is paying for
          // their own license.
          <Button
            mode="contained"
            onPress={() => router.push('/dashboard/self-pay')}
            style={styles.cta}
            contentStyle={styles.ctaContent}
            testID="paywall-expired-selfpay-cta"
          >
            {t('expired.ctaDriver', 'Pagar a minha licença')}
          </Button>
        )}

        {error && (
          <Text style={[styles.error, { color: theme.colors.error }]} accessibilityRole="alert">
            {error}
          </Text>
        )}
      </View>

      <PlanSelectionModal
        visible={plansVisible && !gpsContractGate.visible}
        onDismiss={() => {
          // ALL-134: closing the plan list forgets the decline, so a later visit starts clean.
          setDeclinedFrom(null);
          setPlansVisible(false);
        }}
        onPlanSelected={handlePlanSelected}
        isTrial={false}
        notice={declinedFrom ? <GpsContractDeclinedNotice /> : null}
      />

      {gpsContractGate.contractModal}

      {clientSecret && (
        <HandlePayment
          visible={paymentVisible}
          onDismiss={() => setPaymentVisible(false)}
          clientSecret={clientSecret}
          priceId={selected?.plan.price_id}
          quantity={selected?.quantity ?? 1}
          isTrial={false}
          subscriptionId={subscriptionId || undefined}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    padding: 24,
  },
  content: {
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
  },
  title: {
    textAlign: 'center',
    marginBottom: 12,
  },
  body: {
    textAlign: 'center',
    marginBottom: 24,
  },
  cta: {
    borderRadius: 12,
  },
  ctaContent: {
    paddingVertical: 8,
  },
  error: {
    marginTop: 16,
    textAlign: 'center',
  },
});
